import { View } from "../types/view";

const DB_NAME = "calmsteps-offline";
const DB_VERSION = 1;
const STORE_NAME = "views";

export type CachedImage = {
  id: string;
  bytes: ArrayBuffer;
  title: string;
  order: number;
};

export type CachedView = {
  code: string;
  name: string;
  cachedAt: number;
  images: CachedImage[];
};

let dbPromise: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: "code" });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }
  return dbPromise;
}

export async function cacheView(view: View): Promise<void> {
  const db = await openDB();
  const cached: CachedView = {
    code: view.code,
    name: view.name,
    cachedAt: Date.now(),
    images: view.images.map((img) => ({
      id: img.id.toString(),
      bytes: img.bytes as ArrayBuffer,
      title: img.title,
      order: img.order,
    })),
  };
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const req = tx.objectStore(STORE_NAME).put(cached);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export async function getCachedView(code: string): Promise<CachedView | null> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const req = tx.objectStore(STORE_NAME).get(code);
    req.onsuccess = () => resolve((req.result as CachedView | undefined) ?? null);
    req.onerror = () => reject(req.error);
  });
}

export async function deleteCachedView(code: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const req = tx.objectStore(STORE_NAME).delete(code);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}
